import { Link } from '@tanstack/react-router'
import { LifeBuoy } from 'lucide-react'
import { useT } from '~/lib/i18n'
import { anchor } from '~/lib/tour/anchors.ts'
import { useTour } from '~/lib/tour/state.tsx'
import { cn } from '~/lib/utils'

/**
 * The way back into the guide, in the two shapes the shell has room for.
 *
 * Both carry the same anchor and only one of them is ever on screen — the sidebar
 * is `display:none` on a phone and the header strip is `lg:hidden` — so the
 * walkthrough's own "this button" step finds whichever one this viewport actually
 * has (see `findAnchor`).
 *
 * The dot stays until the everyday walkthrough has been through once: somebody who
 * skipped the introduction should still be able to see that there was one.
 */
function useUnopened(): boolean {
  return !useTour().hasSeen('basics')
}

/**
 * The sidebar's copy: an icon *and* the words.
 *
 * It sits in the footer beside the theme and language switches rather than up in
 * the header beside the bell. The header is 17rem wide and already holds the mark,
 * the wordmark and the org name; a second icon button up there cost the lockup
 * enough width to ellipsis "Hardware Mediator" into "Hardware …". Down here it can
 * say what it is, which is better than a life-ring nobody is obliged to recognise.
 */
export function GuideLink({ className }: { className?: string }) {
  const t = useT()
  const unopened = useUnopened()

  return (
    <Link
      {...anchor('shell.guide')}
      to="/guide"
      className={cn(
        'flex items-center gap-2.5 rounded-control px-2.5 py-2 text-sm font-semibold',
        'text-ink-muted transition-colors hover:bg-surface-muted hover:text-ink',
        className,
      )}
    >
      <LifeBuoy className="size-4 shrink-0" aria-hidden />
      <span className="min-w-0 flex-1 truncate">{t('tour.show_me_around')}</span>
      {unopened ? <span className="size-2 shrink-0 rounded-full bg-primary" aria-hidden /> : null}
    </Link>
  )
}

/** The phone's copy, in the header strip, where there is no footer to put it in. */
export function GuideButton({ className }: { className?: string }) {
  const t = useT()
  const unopened = useUnopened()

  return (
    <Link
      {...anchor('shell.guide')}
      to="/guide"
      aria-label={t('tour.show_me_around')}
      title={t('tour.show_me_around')}
      className={cn(
        'relative grid size-10 shrink-0 place-items-center rounded-control text-ink-muted transition-colors hover:bg-surface-muted hover:text-ink',
        className,
      )}
    >
      <LifeBuoy className="size-5" />
      {unopened ? (
        <span
          className="absolute top-1.5 right-1.5 size-2 rounded-full bg-primary ring-2 ring-surface"
          aria-hidden
        />
      ) : null}
    </Link>
  )
}
